import { Children, ReactNode, UIEventHandler } from 'react';
import { SliderButton } from './sliderButton';
import { useHorizontalScroll } from './useHorizontalScroll';

type TProps = {
  children: ReactNode;
  onScroll?: (x: number) => void;
  hovered?: boolean;
  bottomContent?: ReactNode[];
};

export const Slider = ({ children, onScroll, hovered, bottomContent }: TProps) => {
  const totalItems = Children.count(children);

  const {
    scrollContainerRef,
    handleScroll,
    scrollLeft,
    scrollRight,
    isAtStart,
    isAtEnd,
    currentIndex,
    scrollToIndex,
  } = useHorizontalScroll({ totalItems });

  const scrollHandler: UIEventHandler<HTMLDivElement> = (e) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    onScroll?.(scrollLeft);
    handleScroll?.(e);
  };

  const handleDotClick = (index: number) => {
    scrollToIndex(index);
  };

  return (
    <div className="relative">
      {/* Mobile slider */}
      <div
        ref={scrollContainerRef}
        onScroll={scrollHandler}
        className="tablet:hidden overflow-x-auto snap-x snap-mandatory scrollbar-hide
                scroll-px-sm transition-all pt-xs pb-sm -my-xs flex gap-2xs"
        data-testid="slider-slides-container-mobile"
      >
        <div className="p-lg snap-start"></div>
        {children}
        <div className="p-lg snap-start"></div>
      </div>

      {/* Desktop slider */}
      <div
        ref={scrollContainerRef}
        data-hydrate="mouseover"
        onScroll={scrollHandler}
        className="hidden tablet:flex overflow-x-auto snap-x snap-mandatory scrollbar-hide
                scroll-px-sm transition-all pt-xs pb-sm -my-xs flex gap-2xs"
        data-testid="slider-slides-container-desktop"
      >
        <div className="p-lg snap-start"></div>
        {children}
        <div className="p-lg snap-start"></div>
      </div>

      {/* Navigation buttons */}
      <SliderButton
        scrollContainerRef={scrollContainerRef}
        position="left"
        hidden={isAtStart || !hovered}
        onClick={scrollLeft}
      />

      <SliderButton
        scrollContainerRef={scrollContainerRef}
        position="right"
        hidden={isAtEnd || !hovered}
        onClick={scrollRight}
      />

      {/* Dots navigation */}
      {totalItems > 1 && (
        <div className="flex justify-center mt-md mb-md">
          {Array.from({ length: totalItems }).map((_, index) => (
            <div
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-2.5 h-2.5 rounded-full mx-3xs cursor-pointer ${
                index === currentIndex ? 'bg-purple-0' : 'bg-purple-90'
              }`}
            />
          ))}
        </div>
      )}

      {/* Bottom content that syncs with the slider */}
      {bottomContent && bottomContent.length > 0 && (
        <div className="flex justify-center w-full">
          <div className="md:w-[76%] w-[64%] text-center">
            {bottomContent[currentIndex] || bottomContent[0]}
          </div>
        </div>
      )}
    </div>
  );
};
